import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarPlus } from "@fortawesome/free-regular-svg-icons";
import { faInfoCircle, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import DateAccordion from "@/components/common/DateAccordion";
import DecisionsFirst from "./DecisionsFirst";
import SubsequentDecisions from "./SubsequentDecisions";
import { calculateDecisionTimes } from "../services/waveDecisionService";
import TooltipIconButton from "@/components/common/TooltipIconButton";
import CommonSwitch from "@/components/utils/switch/CommonSwitch";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
interface DecisionsProps {
  readonly dates: CreateWaveDatesConfig;
  readonly errors: CREATE_WAVE_VALIDATION_ERROR[];
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
  readonly onRollingEnabled: () => void;
  readonly isExpanded: boolean;
  readonly setIsExpanded: (expanded: boolean) => void;
  readonly onInteraction: () => void;
}
interface DecisionsCollapsedContentProps {
  readonly totalDecisionPoints: number;
  readonly isRollingMode: boolean;
}
function DecisionsCollapsedContent(t0) {
  const $ = _c(10);
  const {
    totalDecisionPoints,
    isRollingMode
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FontAwesomeIcon icon={faCalendarPlus} className="tw-mr-2 tw-size-4 tw-text-primary-400" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Winners</p>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = totalDecisionPoints !== 1 ? "s" : "";
  let t4;
  if ($[2] !== t3 || $[3] !== totalDecisionPoints) {
    t4 = <span className="tw-text-sm tw-font-medium tw-text-iron-50">{totalDecisionPoints} announcement{t3}</span>;
    $[2] = t3;
    $[3] = totalDecisionPoints;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isRollingMode) {
    t5 = isRollingMode && <span className="tw-ml-2 tw-rounded tw-bg-blue-500/20 tw-px-1.5 tw-text-xs tw-text-blue-400">Recurring</span>;
    $[5] = isRollingMode;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="tw-flex tw-items-center tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]">{t1}<div className="tw-flex tw-flex-col">{t2}<div className="tw-flex tw-items-center">{t4}{t5}</div></div></div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
export default function Decisions(t0) {
  const $ = _c(65);
  const {
    dates,
    errors,
    setDates,
    onRollingEnabled,
    isExpanded,
    setIsExpanded,
    onInteraction
  } = t0;
  const isRollingMode = dates.isRolling;
  let t1;
  if ($[0] !== errors) {
    t1 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.RANK_DECISION_TIME_MUST_BE_IN_FUTURE);
    $[0] = errors;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasRankFutureDateError = t1;
  let t2;
  if ($[2] !== errors) {
    t2 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.END_DATE_MUST_BE_AFTER_VOTING_START_DATE);
    $[2] = errors;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasEndDateBeforeVotingStartError = t2;
  let t3;
  if ($[4] !== errors) {
    t3 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.RANK_FIRST_DECISION_TIME_MUST_BE_AFTER_OR_EQUAL_TO_VOTING_START_DATE);
    $[4] = errors;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasFirstDecisionBeforeVotingStartError = t3;
  const shouldShowExpandedContent = isExpanded || hasRankFutureDateError || hasEndDateBeforeVotingStartError || hasFirstDecisionBeforeVotingStartError;
  const totalDecisionPoints = 1 + dates.subsequentDecisions.length;
  let t4;
  if ($[6] !== dates || $[7] !== setDates) {
    t4 = decisions => {
      setDates({
        ...dates,
        subsequentDecisions: decisions
      });
    };
    $[6] = dates;
    $[7] = setDates;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleUpdateSubsequentDecisions = t4;
  let t5;
  if ($[9] !== dates || $[10] !== onRollingEnabled || $[11] !== setDates) {
    t5 = value => {
      if (value && dates.subsequentDecisions.length === 0) {
        return;
      }
      if (value) {
        onRollingEnabled();
        setDates({
          ...dates,
          isRolling: true,
          endDate: null
        });
      } else {
        let newEndDate = dates.firstDecisionTime;
        if (dates.subsequentDecisions.length > 0) {
          const decisionTimes = calculateDecisionTimes(dates.firstDecisionTime, dates.subsequentDecisions);
          const potentialNewEndDate = decisionTimes[decisionTimes.length - 1];
          if (potentialNewEndDate !== undefined) {
            newEndDate = potentialNewEndDate;
          }
        }
        setDates({
          ...dates,
          isRolling: false,
          endDate: newEndDate
        });
      }
    };
    $[9] = dates;
    $[10] = onRollingEnabled;
    $[11] = setDates;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleToggleSwitch = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TooltipIconButton icon={faInfoCircle} tooltipText="Schedule when winners will be announced during your wave. With recurring cycles, announcements repeat until an optional end date." tooltipPosition="bottom" tooltipWidth="tw-w-80" />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== setIsExpanded || $[15] !== shouldShowExpandedContent) {
    t7 = () => setIsExpanded(!shouldShowExpandedContent);
    $[14] = setIsExpanded;
    $[15] = shouldShowExpandedContent;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== isRollingMode || $[18] !== totalDecisionPoints) {
    t8 = <DecisionsCollapsedContent totalDecisionPoints={totalDecisionPoints} isRollingMode={isRollingMode} />;
    $[17] = isRollingMode;
    $[18] = totalDecisionPoints;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== hasRankFutureDateError) {
    t9 = hasRankFutureDateError && <div role="alert" className="tw-mb-3 tw-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-error/40 tw-bg-error/10 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-error"><FontAwesomeIcon icon={faTriangleExclamation} className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" /><span>First winners announcement and wave end must be in the future.</span></div>;
    $[20] = hasRankFutureDateError;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== hasEndDateBeforeVotingStartError) {
    t10 = hasEndDateBeforeVotingStartError && <div role="alert" className="tw-mb-3 tw-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-error/40 tw-bg-error/10 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-error"><FontAwesomeIcon icon={faTriangleExclamation} className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" /><span>Last winners announcement cannot be before voting begins. Move voting start earlier or move winner announcements later.</span></div>;
    $[22] = hasEndDateBeforeVotingStartError;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== hasFirstDecisionBeforeVotingStartError) {
    t11 = hasFirstDecisionBeforeVotingStartError && <div role="alert" className="tw-mb-3 tw-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-error/40 tw-bg-error/10 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-error"><FontAwesomeIcon icon={faTriangleExclamation} className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" /><span>First winners announcement cannot be before voting begins. Move voting start earlier or move first winners announcement later.</span></div>;
    $[24] = hasFirstDecisionBeforeVotingStartError;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <strong>Winner announcements</strong>;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== dates.subsequentDecisions.length) {
    t13 = dates.subsequentDecisions.length === 0 && <span className="tw-text-primary-300">{" "}With a fixed schedule, the last announcement marks your wave's end date.</span>;
    $[27] = dates.subsequentDecisions.length;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13) {
    t14 = <p className="tw-mb-0 tw-text-sm tw-text-iron-300">{t12} for showcasing selected creators. Set your first date, then add more if needed.{t13}</p>;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-italic tw-text-iron-400">Examples: Weekly, monthly, or quarterly announcements.</p>;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t14) {
    t16 = <div className="tw-mb-3 tw-border-b tw-border-iron-700/50 tw-pb-3">{t14}{t15}</div>;
    $[32] = t14;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t16 || $[37] !== t9) {
    t17 = <div className="tw-px-5 tw-pb-1 tw-pt-2">{t9}{t10}{t11}{t16}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t16;
    $[37] = t9;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== dates || $[40] !== setDates) {
    t18 = time => setDates({
      ...dates,
      firstDecisionTime: time
    });
    $[39] = dates;
    $[40] = setDates;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== dates.firstDecisionTime || $[43] !== dates.votingStartDate || $[44] !== t18) {
    t19 = <DecisionsFirst firstDecisionTime={dates.firstDecisionTime} setFirstDecisionTime={t18} minTimestamp={dates.votingStartDate} />;
    $[42] = dates.firstDecisionTime;
    $[43] = dates.votingStartDate;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== dates.firstDecisionTime || $[47] !== dates.subsequentDecisions || $[48] !== handleUpdateSubsequentDecisions) {
    t20 = <div className="tw-col-span-2"><SubsequentDecisions firstDecisionTime={dates.firstDecisionTime} subsequentDecisions={dates.subsequentDecisions} setSubsequentDecisions={handleUpdateSubsequentDecisions} /></div>;
    $[46] = dates.firstDecisionTime;
    $[47] = dates.subsequentDecisions;
    $[48] = handleUpdateSubsequentDecisions;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== dates.subsequentDecisions.length || $[51] !== handleToggleSwitch || $[52] !== isRollingMode) {
    t21 = dates.subsequentDecisions.length > 0 && <div className="tw-col-span-2"><div className="tw-mb-2 tw-mt-5 tw-rounded-lg tw-border tw-border-iron-700/50 tw-shadow-md"><div className="tw-flex tw-items-center tw-justify-between"><div className="tw-flex-1"><h3 className="tw-mb-1 tw-text-base tw-font-semibold tw-text-iron-50">Repeating Announcement Cycles</h3><p className="tw-mb-0 tw-text-xs tw-text-iron-400">Repeat this pattern until an optional end date</p></div><div><CommonSwitch label="Enable recurring cycles" isOn={isRollingMode} setIsOn={handleToggleSwitch} /></div></div>{isRollingMode && <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-primary-500/30 tw-bg-primary-500/20 tw-p-3 tw-shadow-inner"><p className="tw-text-primary-100 tw-mb-0 tw-text-xs"><strong>Recurring cycles enabled.</strong> Announcements will repeat until an optional end date, or keep going with no end date.</p></div>}</div></div>;
    $[50] = dates.subsequentDecisions.length;
    $[51] = handleToggleSwitch;
    $[52] = isRollingMode;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== onInteraction || $[55] !== t19 || $[56] !== t20 || $[57] !== t21) {
    t22 = <div className="tw-grid tw-grid-cols-1 tw-gap-x-10 tw-gap-y-6 tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700 tw-px-5 tw-pb-5 md:tw-grid-cols-2" onClick={onInteraction}>{t19}{t20}{t21}</div>;
    $[54] = onInteraction;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== shouldShowExpandedContent || $[60] !== t17 || $[61] !== t22 || $[62] !== t7 || $[63] !== t8) {
    t23 = <DateAccordion title="Winners Announcements" titleActions={t6} isExpanded={shouldShowExpandedContent} onToggle={t7} collapsedContent={t8}>{t17}{t22}</DateAccordion>;
    $[59] = shouldShowExpandedContent;
    $[60] = t17;
    $[61] = t22;
    $[62] = t7;
    $[63] = t8;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  return t23;
}